import { type Ellipse, type Segment } from '../geometry';
import { type Vec2, vec } from '../math';

import { buildMesh, type Mesh, SolidKind, type SolidParameters } from './mesh';
import { isConvexMesh, occludeSegment, viewerDirection } from './occlude';
import { type RoundBody } from './plane';
import { createProjector, eyeDistanceFor, ProjectionKind, type Projector } from './project';
import { add as add3, centroid, newellNormal, type Euler, vec3, type Vec3 } from './Vec3';

/**
 * Projecting a solid to a drawn wireframe, with hidden edges separated out.
 *
 * ## How visibility is decided
 *
 * By classifying faces, not by sorting depths. Every solid the brief asks for is **convex**,
 * and for a convex solid the rule is exact rather than approximate:
 *
 * - an edge whose adjacent faces *all* point away from the viewer is hidden;
 * - an edge with a face on each side is a silhouette, and always visible;
 * - an edge whose faces all point towards the viewer is visible.
 *
 * No painter's algorithm, no depth buffer, no edge splitting. The result is exact at any
 * zoom, which a raster method is not, and it is the reason a hidden line here is genuinely
 * hidden rather than merely drawn earlier.
 *
 * **This is where the convexity assumption lives.** A non-convex solid — an L-shaped block,
 * a torus — can hide an edge behind a face that is not adjacent to it, and this function
 * would draw that edge solid. Adding one means adding edge-vs-face clipping, which is a
 * different algorithm rather than a parameter to this one. Every primitive in `mesh.ts` is
 * convex, and that is not an accident.
 *
 * ## Smooth edges
 *
 * An edge that only exists because a curved surface was tessellated is drawn *only* where
 * it is a silhouette. That turns a mesh of forty-eight vertical lines into the two straight
 * sides a cylinder is actually drawn with.
 *
 * ## The sphere is the one that cannot be done this way
 *
 * For a cylinder, a cone or a frustum the outline runs *along* mesh edges — the generator
 * lines — so recovering it from the grid is exact. For a sphere it does not. The outline
 * wanders diagonally across a latitude/longitude grid and comes back as a **staircase**:
 * lumpy, unevenly stepped, and visibly not round.
 *
 * More tessellation does not fix it. Measured: going from 12 bands to 40 makes the outline
 * rounder and never smooth, costs 2.2× the projection time, and *breaks the dashed rings* —
 * each short segment restarts the dash pattern, so a chain of them renders as a continuous
 * grey line rather than a dashed one.
 *
 * The outline of a sphere under a parallel projection is **exactly an ellipse**, so it is
 * drawn as one. See {@link sphereOutline}.
 */

/**
 * A curve a solid is drawn with, rather than a chain of chords.
 *
 * ## Why a solid needs one at all
 *
 * Because a polyline cannot survive being zoomed into, and this editor promises that its
 * figures are exact at every magnification. A sphere drawn as seventy-two chords is smooth on
 * the page and a visibly kinked chain at 8×, which is the state this type exists to end.
 *
 * It is the way a teacher draws a sphere at the board: **a circle, and an ellipse across it**.
 * Both are real curves there, and both are real curves here — the canvas gets an arc, an SVG
 * gets an `<ellipse>` or an `A` path, a PDF gets Béziers. None of them is sampled.
 *
 * `from` and `sweep` are in the ellipse's own parameter, measured from its first axis. Absent
 * means the whole ring, which is what the outline is.
 */
export interface SolidCurve {
  readonly ellipse: Ellipse;
  readonly from?: number;
  readonly sweep?: number;
  /** Behind the body: drawn dashed, exactly as a hidden edge is. */
  readonly hidden: boolean;
}

export interface Wireframe {
  /** Edges facing the viewer. Drawn solid. */
  readonly visible: readonly Segment[];
  /** Edges behind the solid. Drawn dashed, or omitted when hidden lines are off. */
  readonly hidden: readonly Segment[];
  /**
   * The parts of the figure that are exactly conics.
   *
   * A sphere's outline and rings; the rims of a cylinder, a cone or a frustum. Absent under
   * perspective, where the argument for an exact ellipse does not hold, and absent for a solid
   * with nothing round about it.
   */
  readonly curves?: readonly SolidCurve[];
  /**
   * What a curve-aware painter draws *beside* the curves — present whenever `curves` is.
   *
   * The two representations serve different readers, which is why both are here:
   *
   * - `visible` and `hidden` are the complete figure as a polyline. Bounds, hit-testing and the
   *   spatial index read those, and none of them should have to learn about conics to frame a
   *   picture or decide what a click landed on.
   * - `curves` and `straight` are the same figure for someone who is going to *draw* it. The
   *   rim chords are missing from `straight` precisely because the curve already covers them:
   *   drawing both would lay a polyline over its own ellipse, which thickens on export and
   *   shows as a shadow beside the curve at any real magnification.
   *
   * For a sphere `straight` is empty — the curves are the whole drawing. For a cylinder it is
   * the two sides, which are generator lines and really are straight.
   */
  readonly straight?: { readonly visible: readonly Segment[]; readonly hidden: readonly Segment[] };
  /**
   * The corners and edges kept as a numbering, for a solid whose edges are countable things.
   *
   * Present for a cube, a cuboid, a prism and a pyramid; absent for the round solids and the
   * sphere. The distinction is not about difficulty — the lattice exists either way — but about
   * whether an "edge" is a fact about the *object*. A pyramid's `SA` is; a cylinder's drawn
   * sides are two silhouette generators picked out of forty-eight verticals by where the reader
   * happens to stand, and they move to different vertices the moment the solid turns. Numbering
   * those would hand out names that do not survive a rotation.
   *
   * This is what lets an edge be lifted out and given its own colour or dash — see the note on
   * {@link projectLattice} about handsome boxes no problem can be stated about.
   */
  readonly corners?: SolidCorners;
  /**
   * The solid as the few numbers a section of a *round* one is worked out from.
   *
   * Present for a cylinder, a cone, a frustum and a ball, and absent for the polyhedra, which
   * are cut from their mesh instead. Carried beside `frame`, because a round section is a circle
   * in space and the projection is what turns it into the exact ellipse a reader sees — rather
   * than into the chain of chords a tessellated cut would have given, which is the whole of what
   * the rim work was about.
   */
  readonly round?: { readonly body: RoundBody; readonly frame: SolidFrame };
}

/** A solid's own numbering: where each corner is, on the page and in space, and what joins them. */
export interface SolidCorners {
  readonly points: readonly Vec2[];
  readonly in3d: readonly Vec3[];
  /** Drawn edges, as pairs of indices into {@link points}. */
  readonly edges: readonly (readonly [number, number])[];
  /** Face rings, for the half-space tests a plane needs. */
  readonly faces: readonly (readonly number[])[];
  /**
   * The projection itself, as the images of the origin and the three unit axes.
   *
   * Four points settle a parallel projection completely, because it is affine: the image of
   * `(x, y, z)` is `origin + x·ex + y·ey + z·ez`. Carrying them is what lets something worked
   * out in *space* — the section of a plane, the chord of a line — be placed on the page
   * afterwards, without the evaluator needing to know which projection built the solid, and
   * without keeping a function in a value.
   */
  readonly frame: SolidFrame;
}

/** A parallel projection, as the images of the origin and the three unit axes. */
export interface SolidFrame {
  readonly origin: Vec2;
  readonly x: Vec2;
  readonly y: Vec2;
  readonly z: Vec2;
}

/** Where a point in space lands on the page, under a solid's own projection. */
export function onPageOf(frame: SolidFrame, point: Vec3): Vec2 {
  return {
    x: frame.origin.x + point.x * frame.x.x + point.y * frame.y.x + point.z * frame.z.x,
    y: frame.origin.y + point.x * frame.x.y + point.y * frame.y.y + point.z * frame.z.y,
  };
}

/** Solids a plane cuts in a circle rather than in a polygon. */
const ROUND_BODIES: readonly SolidKind[] = [
  SolidKind.Cylinder,
  SolidKind.Cone,
  SolidKind.Frustum,
  SolidKind.Sphere,
];

/**
 * The round solid as radii and a taper.
 *
 * `taper` is the top radius as a fraction of the bottom, which is how `buildMesh` describes them
 * too: 1 for a cylinder, 0 for a cone, and the user's number for a frustum — so one formula
 * covers all three and a cone needs no case of its own.
 */
function roundBodyOf(parameters: SolidParameters): RoundBody {
  const taper =
    parameters.kind === SolidKind.Cone
      ? 0
      : parameters.kind === SolidKind.Frustum
        ? Math.max(0.01, Math.min(1, parameters.taper))
        : 1;

  return {
    ball: parameters.kind === SolidKind.Sphere,
    rx: parameters.width / 2,
    ry: parameters.height / 2,
    rz: parameters.depth / 2,
    taper,
  };
}

/** Solids whose edges are countable facts about the object rather than tessellation artefacts. */
const COUNTABLE: readonly SolidKind[] = [
  SolidKind.Cube,
  SolidKind.Cuboid,
  SolidKind.Prism,
  SolidKind.Pyramid,
];

export const EMPTY_WIREFRAME: Wireframe = Object.freeze({ visible: [], hidden: [] });

export interface SolidView {
  readonly parameters: SolidParameters;
  readonly rotation: Euler;
  readonly projection: ProjectionKind;
  /** Where the solid's centre lands on the drawing plane. */
  readonly origin: Vec2;
}

export function projectSolid(view: SolidView): Wireframe {
  const mesh = buildMesh(view.parameters);
  return projectMesh(mesh, view);
}

/**
 * The same projection, with the mesh's own numbering kept.
 *
 * ## Why this exists beside `projectMesh`
 *
 * A `Wireframe` is a bag of line segments: correct to draw, and useless to build on. Nothing
 * in it says that this endpoint and that endpoint are the *same corner*, so a caller cannot
 * name a vertex, hang a midpoint off an edge, or ask where `A` went. That is exactly the
 * complaint the built-in solids earned — they draw a handsome box that no problem can be
 * stated about, and every problem in the exercise book names its vertices in the first line.
 *
 * So the classification is done once, here, and reported *per index*. `projectMesh` is a
 * projection of this and stays the right thing for a caller that only wants to paint.
 */
export interface LatticeEdge {
  /** Indices into {@link Lattice.points}. */
  readonly a: number;
  readonly b: number;
  /** True when every face meeting this edge points away: the edge is behind the solid. */
  readonly hidden: boolean;
  /** False for an edge that exists only because a curved surface was tessellated. */
  readonly hard: boolean;
  /** True when the edge has a front face on one side and a back face on the other. */
  readonly silhouette: boolean;
  /**
   * Where the body covers this edge, as stretches along it, for a solid that is not convex.
   *
   * Absent for a convex one, which is every primitive the product ships: there `hidden` is the
   * whole answer and costs one comparison. An L-shaped body needs more than a flag — its far edge
   * is covered in the middle and drawn at both ends — and this carries that without changing what
   * a convex solid computes or how it is drawn.
   */
  readonly pieces?: readonly {
    readonly from: number;
    readonly to: number;
    readonly hidden: boolean;
  }[];
}

export interface Lattice {
  /** One point per mesh vertex, in the mesh's order. */
  readonly points: readonly Vec2[];
  readonly edges: readonly LatticeEdge[];
}

export function projectLattice(mesh: Mesh, view: SolidView): Lattice {
  /*
   * The eye is placed relative to the solid's own size. A fixed distance works until the
   * solid grows to reach it and the projection divides by zero; scaling it keeps the eye
   * outside at every size, so the failure cannot occur rather than being clamped after the
   * fact. Parallel projections ignore it.
   */
  return projectWith(
    mesh,
    createProjector(view.projection, view.rotation, eyeDistanceFor(boundingRadius(mesh))),
    view.origin
  );
}

/**
 * The same, for a caller that has its own projector.
 *
 * Exists because the three built-in projections encode a *convention* about where the eye is,
 * and a caller can legitimately want a different one. The named solids do: a cabinet drawing
 * with the eye at `+z` puts the near face up and to the right, which is a valid oblique view
 * and not the one any textbook uses — theirs has the near face low and left with the depth
 * running away up-right. That is the same shear seen from the other side, and expressing it
 * means supplying a projector rather than choosing from a list.
 *
 * Whatever the projector says about facing is taken as given. The classification below is
 * exact for a convex solid and says nothing about where the eye is; keeping that decision in
 * the projector is what lets one algorithm serve both.
 */
export function projectWith(mesh: Mesh, projector: Projector, origin: Vec2): Lattice {
  const inView: Vec3[] = mesh.vertices.map((vertex) => projector.toView(vertex));
  const planar: Vec2[] = inView.map((vertex) => {
    const point = projector.toPlane(vertex);
    return { x: point.x + origin.x, y: point.y + origin.y };
  });

  // Face orientation is computed once and read many times: a cube's six faces are each
  // consulted by four edges, and a sphere's by two.
  const frontFacing = mesh.faces.map((ring) => {
    const points = ring
      .map((index) => inView[index])
      .filter((point): point is Vec3 => point !== undefined);
    if (points.length < 3) return false;
    return projector.isFrontFacing(newellNormal(points), centroid(points));
  });

  /*
   * Which rule decides visibility, and why there are two.
   *
   * Classifying faces — "hidden when every face meeting the edge points away" — is exact for a
   * **convex** body and costs one comparison per edge. It is blind to a face that is not adjacent
   * to the edge, so an L-shaped body draws its far edge solid straight through the arm standing
   * in front of it: wrong, and wrong in the way that looks plausible.
   *
   * So a body that is not convex is cut instead, exactly, against its own faces. That is a pass
   * over every face for every edge, which a forty-eight-sided cylinder would feel on every frame
   * of a drag — and never has to, because it is convex. Every primitive in `mesh.ts` is, which is
   * why this change moves no figure that exists today.
   *
   * Under perspective the cutter has no footing (`viewerDirection` returns nothing) and the old
   * rule stands, for the same reason the exact conics stand down there.
   */
  const toViewer = isConvexMesh(mesh) ? null : viewerDirection(projector);
  const facesInView =
    toViewer === null
      ? null
      : mesh.faces.map((ring) =>
          ring.flatMap((index) => {
            const point = inView[index];
            return point === undefined ? [] : [point];
          })
        );

  const edges: LatticeEdge[] = mesh.edges.map((edge) => {
    const facing = edge.faces.map((index) => frontFacing[index] ?? false);
    const anyFront = facing.some(Boolean);
    const anyBack = facing.some((value) => !value);
    const classified = {
      a: edge.a,
      b: edge.b,
      hidden: !anyFront,
      hard: edge.hard,
      silhouette: anyFront && anyBack,
    };

    if (toViewer === null || facesInView === null) return classified;

    const from = inView[edge.a];
    const to = inView[edge.b];
    if (from === undefined || to === undefined) return classified;

    const pieces = occludeSegment(from, to, facesInView, {
      project: (point) => {
        const image = projector.toPlane(point);
        return { x: image.x + origin.x, y: image.y + origin.y };
      },
      toViewer,
    });

    return {
      ...classified,
      // `hidden` still answers for the whole edge, so everything that only wants a flag — the
      // silhouette rule above, and any caller that never learned about pieces — keeps working.
      hidden: pieces.every((piece) => piece.hidden),
      pieces,
    };
  });

  return { points: planar, edges };
}

/** Exposed separately so a caller with a mesh in hand does not rebuild it. */
export function projectMesh(mesh: Mesh, view: SolidView): Wireframe {
  const lattice = projectLattice(mesh, view);

  const visible: Segment[] = [];
  const hidden: Segment[] = [];

  // The curves come first, because they decide which edges are left for the painter to draw.
  const curved = solidCurves(mesh, lattice, view);
  const drawnAsCurve = new Set(curved.replaces);

  const straightVisible: Segment[] = [];
  const straightHidden: Segment[] = [];

  for (const edge of lattice.edges) {
    const a = lattice.points[edge.a];
    const b = lattice.points[edge.b];
    if (a === undefined || b === undefined) continue;

    // A tessellation artefact is a real line on the page only where it is a silhouette:
    // that is what turns forty-eight verticals into the two sides a cylinder is drawn with.
    const drawn = edge.hard || edge.silhouette;
    if (!drawn) continue;

    const straight = !drawnAsCurve.has(edgeKey(edge.a, edge.b));

    /*
     * An edge a body covers only in part, which only a non-convex body can produce.
     *
     * `Wireframe` already carries lists of segments rather than a flag per edge, so a cut edge
     * simply contributes several — the interface does not move, and neither does any painter.
     */
    if (edge.hard && edge.pieces !== undefined) {
      for (const piece of edge.pieces) {
        const cut = { a: vec.lerp(a, b, piece.from), b: vec.lerp(a, b, piece.to) };
        (piece.hidden ? hidden : visible).push(cut);
        if (straight) (piece.hidden ? straightHidden : straightVisible).push(cut);
      }
      continue;
    }

    const behind = edge.hard && edge.hidden;
    (behind ? hidden : visible).push({ a, b });
    if (straight) {
      (behind ? straightHidden : straightVisible).push({ a, b });
    }
  }

  const round = ROUND_BODIES.includes(view.parameters.kind)
    ? { body: roundBodyOf(view.parameters), frame: frameOf(view) }
    : undefined;

  const corners = COUNTABLE.includes(view.parameters.kind)
    ? {
        points: lattice.points,
        in3d: mesh.vertices,
        edges: lattice.edges.filter((edge) => edge.hard).map((edge) => [edge.a, edge.b] as const),
        faces: mesh.faces,
        frame: frameOf(view),
      }
    : undefined;

  const extras = {
    ...(corners === undefined ? {} : { corners }),
    ...(round === undefined ? {} : { round }),
  };

  if (curved.curves.length === 0) return { visible, hidden, ...extras };
  return {
    visible,
    hidden,
    curves: curved.curves,
    straight: { visible: straightVisible, hidden: straightHidden },
    ...extras,
  };
}

/**
 * The projection this view applies, as four points.
 *
 * Taken by projecting the origin and the three unit axes, which is four calls and settles an
 * affine map completely. Under perspective the map is not affine and this would be a lie, so the
 * caller is responsible for not asking — the same rule the exact conics already follow.
 */
function frameOf(view: SolidView): SolidFrame {
  return projectionFrame(view.origin, view.rotation, view.projection);
}

/**
 * The same four points, for anything in space that is not a solid.
 *
 * Split out when planes stopped needing a body to hang from. A figure of two planes meeting has
 * no solid in it at all, and it still has to put things worked out in *space* onto the page —
 * which is what a frame is. Extracted rather than written twice, for the reason `dashedIfCovered`
 * is one function: two derivations of one projection are two things that can come to disagree,
 * and the disagreement would show as a figure whose parts are drawn from different viewpoints.
 *
 * Under perspective the map is not affine and four points cannot describe it, so every caller is
 * responsible for not asking — see the note on {@link SolidFrame} and what `solid` does with it.
 */
export function projectionFrame(
  origin: Vec2,
  rotation: Euler,
  projection: ProjectionKind
): SolidFrame {
  const projector = createProjector(projection, rotation, 1);
  const flat = (point: Vec3): Vec2 => {
    const image = projector.toPlane(projector.toView(point));
    return { x: image.x + origin.x, y: image.y + origin.y };
  };

  const at = flat(vec3(0, 0, 0));
  const axis = (x: number, y: number, z: number): Vec2 => {
    const image = flat(vec3(x, y, z));
    return { x: image.x - at.x, y: image.y - at.y };
  };

  return { origin: at, x: axis(1, 0, 0), y: axis(0, 1, 0), z: axis(0, 0, 1) };
}

/** Both ends, in a fixed order, so an edge can be found however it was written. */
export function edgeKey(a: number, b: number): string {
  return a < b ? `${String(a)}:${String(b)}` : `${String(b)}:${String(a)}`;
}

/**
 * Every curve this solid is drawn with, and the edges they take the place of.
 *
 * Two sources, and they are different in kind. A **sphere** has no circular edge at all — its
 * outline is a great circle that depends on where the viewer stands — so all three of its
 * curves are computed from the projection. A **cylinder, cone or frustum** has real circles
 * built into it, declared by the mesh as {@link Rim}s, and each is cut into visible and hidden
 * arcs by asking the classifier that already decided the same question for its chords. Reading
 * the answer rather than recomputing it is what keeps a rim's dashes in exactly the places the
 * rest of the figure puts them.
 */
function solidCurves(
  mesh: Mesh,
  lattice: Lattice,
  view: SolidView
): { curves: readonly SolidCurve[]; replaces: readonly string[] } {
  // Under perspective the projection is not affine, and every argument below rests on that.
  if (view.projection === ProjectionKind.Perspective) return { curves: [], replaces: [] };

  const projector = createProjector(view.projection, view.rotation, 1);

  if (view.parameters.kind === SolidKind.Sphere) {
    const sphere = sphereConics(
      vec3(view.parameters.width / 2, view.parameters.height / 2, view.parameters.depth / 2),
      projector,
      view.origin
    );
    if (sphere === null) return { curves: [], replaces: [] };

    // Every hard edge of a sphere is one of the two rings, and the outline replaces the
    // silhouette the grid would otherwise have drawn.
    return { curves: sphere, replaces: lattice.edges.map((edge) => edgeKey(edge.a, edge.b)) };
  }

  return rimConics(mesh, lattice, projector, view.origin);
}

/**
 * The rims of a mesh, as the ellipses they project to, and the edges they take the place of.
 *
 * Each {@link Rim} is a real circle in space, and a parallel projection carries a circle to an
 * ellipse exactly — so this is not an approximation of the chords, it is what the chords were
 * approximating all along. Each is then cut into visible and hidden arcs by asking the
 * classifier that already decided the same question for those chords. Reading its answer rather
 * than recomputing one keeps a rim's dashes in exactly the places the rest of the figure puts
 * them, and leaves no second rule that could drift from the first.
 *
 * Exported because the named solids build their own lattice — a cylinder written in the script
 * language reaches the page by a different path than one dropped by the shape tool — and there
 * is no reason for only one of them to be round.
 */
export function rimConics(
  mesh: Mesh,
  lattice: Lattice,
  projector: Projector,
  origin: Vec2
): { curves: readonly SolidCurve[]; replaces: readonly string[] } {
  const rims = mesh.rims;
  if (rims === undefined || rims.length === 0) return { curves: [], replaces: [] };

  const flat = (point: Vec3): Vec2 => projector.toPlane(projector.toView(point));
  const hiddenByKey = new Map(lattice.edges.map((edge) => [edgeKey(edge.a, edge.b), edge.hidden]));

  const curves: SolidCurve[] = [];
  const replaces: string[] = [];

  for (const rim of rims) {
    const centre = flat(rim.centre);
    /** The image of a half-diameter, with the centre's own offset removed. */
    const radius = (step: Vec3): Vec2 => {
      const tip = flat(add3(rim.centre, step));
      return { x: tip.x - centre.x, y: tip.y - centre.y };
    };

    const count = rim.vertices.length;
    if (count < 3) continue;

    const built = ellipseFromDiameters(
      { x: centre.x + origin.x, y: centre.y + origin.y },
      radius(rim.u),
      radius(rim.v)
    );
    if (built === null) continue;

    /*
     * Contiguous runs of chords that agree, turned into one arc each.
     *
     * A rim is either wholly in view — the top of a cylinder seen from above — or split into a
     * near half and a far half. Walking the runs handles both without asking which it is, and
     * without a rule of its own that could disagree with the classifier.
     */
    const state = rim.vertices.map((vertex, i) => {
      const key = edgeKey(vertex, rim.vertices[(i + 1) % count] ?? vertex);
      replaces.push(key);
      return hiddenByKey.get(key) ?? false;
    });

    const step = (Math.PI * 2) / count;
    let start = 0;
    while (start < count && state[start] === state[count - 1]) start += 1;
    if (start === count) {
      // Every chord agrees, so the rim is one whole ellipse.
      curves.push({ ellipse: built.ellipse, hidden: state[0] ?? false });
      continue;
    }

    let index = 0;
    while (index < count) {
      const at = (start + index) % count;
      let run = 1;
      while (run < count && state[(start + index + run) % count] === state[at]) run += 1;

      const from = (start + index) * step - built.offset;
      curves.push({
        ellipse: built.ellipse,
        from: built.mirrored ? -from - run * step : from,
        sweep: run * step,
        hidden: state[at] ?? false,
      });
      index += run;
    }
  }

  return { curves, replaces };
}

/**
 * The three curves a sphere is drawn with, or `null` when this is not one.
 *
 * ## The outline
 *
 * A parallel projection is affine, so it carries the sphere's ellipsoid to the plane linearly,
 * and the outline of a linear image of a ball is an ellipse — always, exactly. The map is
 * recovered by projecting the origin and the three unit axes, which pins an affine map down
 * completely.
 *
 * ## The equator and the meridian
 *
 * Both are circles in space, and an affine map carries a circle to an ellipse. Each arrives
 * here as a pair of **conjugate half-diameters** — the images of two perpendicular radii — and
 * {@link ellipseFromDiameters} turns that into the axes an ellipse is written with.
 *
 * Each is then cut in half by the silhouette: the near half is drawn solid, the far half
 * dashed, which is the convention every textbook uses and the only thing that makes the ring
 * read as going *round* the sphere rather than lying across it. The two cutting angles are
 * found by bisecting on the projector's own front-facing test rather than by a formula of our
 * own — for a sphere the outward normal at a point *is* that point, so the test applies
 * directly and stays right for every projection it is asked about.
 *
 * ## Why not under perspective
 *
 * Because there the map is not affine and none of the above holds. The outline is a different
 * conic, and drawing the wrong ellipse exactly would be worse than the grid's staircase —
 * smooth and misplaced is much harder to notice than lumpy. Callers make that check; the
 * perspective projector is simply never handed to this function.
 *
 * Exported for the same reason as {@link rimConics}: the named solids reach the page by their
 * own path, and a sphere written in the script language should be the same circle as one
 * dropped by the shape tool.
 */
export function sphereConics(
  radii: Vec3,
  projector: Projector,
  centre: Vec2,
  rings: 'equator' | 'both' = 'both'
): readonly SolidCurve[] | null {
  const flat = (point: Vec3): Vec2 => projector.toPlane(projector.toView(point));

  const origin = flat(vec3(0, 0, 0));
  const rx = radii.x;
  const ry = radii.y;
  const rz = radii.z;

  /** The image of a radius vector, with the projection's own offset removed. */
  const image = (x: number, y: number, z: number): Vec2 => {
    const point = flat(vec3(x, y, z));
    return { x: point.x - origin.x, y: point.y - origin.y };
  };

  const ex = image(rx, 0, 0);
  const ey = image(0, ry, 0);
  const ez = image(0, 0, rz);

  // The outline: the boundary of the image of the ball, whose matrix is A·Aᵀ.
  const xx = ex.x * ex.x + ey.x * ey.x + ez.x * ez.x;
  const xy = ex.x * ex.y + ey.x * ey.y + ez.x * ez.y;
  const yy = ex.y * ex.y + ey.y * ey.y + ez.y * ez.y;

  const root = symmetricSquareRoot(xx, xy, yy);
  if (root === null) return null;

  const outline = ellipseFromDiameters(
    centre,
    { x: root.xx, y: root.xy },
    { x: root.xy, y: root.yy }
  );
  if (outline === null) return null;

  const curves: SolidCurve[] = [{ ellipse: outline.ellipse, hidden: false }];

  /** Whether the point at angle `t` of a ring faces the viewer. Its normal is itself. */
  const ring = (u: Vec3, v: Vec3): void => {
    const built = ellipseFromDiameters(centre, image(u.x, u.y, u.z), image(v.x, v.y, v.z));
    if (built === null) return;

    const facing = (t: number): boolean => {
      const point = vec3(
        u.x * Math.cos(t) + v.x * Math.sin(t),
        u.y * Math.cos(t) + v.y * Math.sin(t),
        u.z * Math.cos(t) + v.z * Math.sin(t)
      );
      const inView = projector.toView(point);
      return projector.isFrontFacing(inView, inView);
    };

    // A great circle is cut into exactly two halves, so one crossing settles both.
    const crossing = bisectFacingChange(facing);
    if (crossing === null) return;

    for (const [at, hidden] of [
      [crossing, !facing(crossing + Math.PI / 2)],
      [crossing + Math.PI, !facing(crossing + Math.PI + Math.PI / 2)],
    ] as const) {
      const angle = at - built.offset;
      curves.push({
        ellipse: built.ellipse,
        from: built.mirrored ? -angle - Math.PI : angle,
        sweep: Math.PI,
        hidden,
      });
    }
  };

  /*
   * The equator lies in the plane of the base; the meridian passes through the poles. Both are
   * drawn for a sphere the shape tool put down — that is the figure a teacher draws at the
   * board — and only the equator for one a problem named, where the sphere is nearly always
   * circumscribed about something else and a second ring across it is clutter.
   */
  ring(vec3(rx, 0, 0), vec3(0, 0, -rz));
  if (rings === 'both') ring(vec3(rx, 0, 0), vec3(0, ry, 0));

  return curves;
}

/**
 * The circle a horizontal plane cuts out of a sphere, split where it goes round the back.
 *
 * "Mặt phẳng (P) cách tâm O một khoảng d cắt mặt cầu theo một đường tròn" is the sentence, and
 * for every problem built on it — đường tròn giao tuyến, the base of an inscribed cone, the
 * circle a triangle is inscribed in — the figure is the sphere's outline with a small ellipse
 * across it. A teacher who cannot draw that draws it by hand as two flat shapes, and then the
 * far half cannot be dashed, because two flat shapes do not know which of them is behind.
 *
 * **The radius is worked out, never given twice.** `r² + h² = R²` is the theorem the problem is
 * about; asking for both numbers would let a figure be drawn that the theorem forbids.
 *
 * The split is the equator's, unchanged. A point of this circle lies on the sphere too, so the
 * outward normal there is still the point itself — measured from the sphere's centre, which is
 * why `facing` adds the height back before asking. Getting that wrong gives a circle split about
 * its *own* centre: right at the equator, where the two coincide, and increasingly wrong towards
 * the poles, where the whole ring is in front and none of it should be dashed at all.
 *
 * `null` when the plane misses the ball, which is not a drawing with an empty circle on it but
 * no drawing at all — the same answer every construction gives when its inputs do not define it.
 */
export function sphereCircle(
  radius: number,
  height: number,
  projector: Projector,
  centre: Vec2,
  /** Degrees the cutting plane leans off horizontal. See {@link sphereSectionBasis}. */
  tilt = 0
): readonly SolidCurve[] | null {
  const flat = (point: Vec3): Vec2 => projector.toPlane(projector.toView(point));
  const origin = flat(vec3(0, 0, 0));

  /** The image of a vector, with the projection's own offset removed. */
  const image = (x: number, y: number, z: number): Vec2 => {
    const point = flat(vec3(x, y, z));
    return { x: point.x - origin.x, y: point.y - origin.y };
  };

  const squared = radius * radius - height * height;
  // Tangent counts as a miss: a plane touching the ball meets it in a point, and a point drawn
  // as a circle of radius zero is a mark the problem never contained.
  if (squared <= 0) return null;
  const small = Math.sqrt(squared);

  const basis = sphereSectionBasis(height, tilt);
  const lift = image(basis.middle.x, basis.middle.y, basis.middle.z);
  const at = { x: centre.x + lift.x, y: centre.y + lift.y };

  /** A point of the ring at angle `t`, in the sphere's own space. */
  const around = (t: number): Vec3 =>
    vec3(
      basis.middle.x + small * (basis.u.x * Math.cos(t) + basis.v.x * Math.sin(t)),
      basis.middle.y + small * (basis.u.y * Math.cos(t) + basis.v.y * Math.sin(t)),
      basis.middle.z + small * (basis.u.z * Math.cos(t) + basis.v.z * Math.sin(t))
    );

  const built = ellipseFromDiameters(
    at,
    image(small * basis.u.x, small * basis.u.y, small * basis.u.z),
    image(small * basis.v.x, small * basis.v.y, small * basis.v.z)
  );
  if (built === null) return null;

  const facing = (t: number): boolean => {
    // The whole point, not the offset from the ring's middle: the normal runs from the
    // **sphere's** centre, because that is the surface this point lies on.
    const point = around(t);
    const inView = projector.toView(point);
    return projector.isFrontFacing(inView, inView);
  };

  const crossing = bisectFacingChange(facing);
  /*
   * No crossing means the whole ring is on one side of the silhouette, which is the ordinary
   * case near a pole: a circle high on the ball is seen entire, and one behind is hidden entire.
   * Both are one closed ellipse rather than two halves.
   */
  if (crossing === null) return [{ ellipse: built.ellipse, hidden: !facing(0) }];

  // Two halves, each asked about its own midpoint — the equator's rule, written the same way.
  return [crossing, crossing + Math.PI].map((start) => {
    const angle = start - built.offset;
    return {
      ellipse: built.ellipse,
      from: built.mirrored ? -angle - Math.PI : angle,
      sweep: Math.PI,
      hidden: !facing(start + Math.PI / 2),
    };
  });
}

/**
 * Where a sphere's cutting plane sits, and two perpendicular radii of the circle it leaves.
 *
 * ## Why the tilt is measured about the depth axis
 *
 * A ball has no up, so nothing in the *mathematics* prefers one direction to lean in — but the
 * drawing does. Leaning about the depth axis turns the ring in the plane of the page, which is
 * what a reader recognises as "nghiêng" at a glance. Leaning it about the screen-horizontal axis
 * mostly changes how open the ellipse is, which reads as a different distance from the centre
 * rather than as a tilt, and that is the one misreading this clause exists to avoid.
 *
 * ## Why it is shared
 *
 * {@link sphereCircle} draws the ring and `rimPoint` puts points on it, and a figure whose drawn
 * circle and whose `rim 0` disagree about where the ring is would be wrong in the way that is
 * hardest to see: both look right alone. One basis, two callers.
 */
export function sphereSectionBasis(
  height: number,
  tilt: number
): { readonly middle: Vec3; readonly u: Vec3; readonly v: Vec3 } {
  const radians = (tilt * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  // The plane's unit normal, upright at 0° and leaning towards +x as the angle grows.
  const normal = vec3(sin, cos, 0);
  return {
    middle: vec3(height * normal.x, height * normal.y, height * normal.z),
    // Perpendicular to the normal and to each other, and both of unit length.
    u: vec3(cos, -sin, 0),
    v: vec3(0, 0, -1),
  };
}

/** Steps of the search for where a ring crosses the silhouette, good to a millionth of a turn. */
const FACING_STEPS = 60;

/** An angle at which the ring passes from facing the viewer to facing away. */
function bisectFacingChange(facing: (t: number) => boolean): number | null {
  const at = facing(0);
  let low = 0;
  let high = 0;

  // A great circle changes exactly twice, so a coarse sweep finds a bracket without fail.
  for (let step = 1; step <= 36; step++) {
    const t = (step / 36) * Math.PI * 2;
    if (facing(t) !== at) {
      low = ((step - 1) / 36) * Math.PI * 2;
      high = t;
      break;
    }
  }
  if (high === 0) return null;

  for (let step = 0; step < FACING_STEPS; step++) {
    const middle = (low + high) / 2;
    if (facing(middle) === at) low = middle;
    else high = middle;
  }
  return (low + high) / 2;
}

/**
 * The ellipse traced by `centre + a·cos t + b·sin t`, and where its parameter starts.
 *
 * `a` and `b` are *conjugate* half-diameters — the images of two perpendicular radii — which
 * an affine map gives back and which are not the axes. The rotation below is the one angle at
 * which they become perpendicular again, and it is exact: for the quadratic form involved the
 * turning points have a closed form.
 *
 * `offset` is what an arc's angle has to be shifted by, because the caller measures `t` from
 * its own first radius and the ellipse measures from its major axis.
 */
export function ellipseFromDiameters(
  centre: Vec2,
  a: Vec2,
  b: Vec2
): { ellipse: Ellipse; offset: number; mirrored: boolean } | null {
  const twist =
    0.5 * Math.atan2(2 * (a.x * b.x + a.y * b.y), a.x * a.x + a.y * a.y - (b.x * b.x + b.y * b.y));

  const along = (t: number): Vec2 => ({
    x: a.x * Math.cos(t) + b.x * Math.sin(t),
    y: a.y * Math.cos(t) + b.y * Math.sin(t),
  });

  const first = along(twist);
  const second = along(twist + Math.PI / 2);

  const rx = Math.hypot(first.x, first.y);
  const ry = Math.hypot(second.x, second.y);
  if (rx < 1e-9 || ry < 1e-9) return null;

  /*
   * The two axes must turn the same way the ellipse parameter does, or every arc comes out
   * mirrored — visible where it should be dashed, which is the one mistake this figure must
   * not make.
   */
  const handed = first.x * second.y - first.y * second.x >= 0;
  const rotation = Math.atan2(first.y, first.x);

  return {
    ellipse: { center: centre, rx, ry, rotation },
    offset: twist,
    // A negative radius is not a thing an ellipse has, so a left-handed pair is expressed by
    // running the parameter backwards instead.
    mirrored: !handed,
  };
}

/** The square root of a 2×2 symmetric positive matrix, itself symmetric.
 *
 * By the closed form `√M = (M + √det·I) / √(trace + 2√det)`, which needs no eigenvectors and
 * has no branch to get wrong. `null` for a degenerate matrix — a sphere flattened to a line by
 * its projection, which none of these do, but which would otherwise divide by zero.
 */
function symmetricSquareRoot(
  xx: number,
  xy: number,
  yy: number
): { xx: number; xy: number; yy: number } | null {
  const determinant = xx * yy - xy * xy;
  if (determinant <= 0) return null;

  const s = Math.sqrt(determinant);
  const t = Math.sqrt(xx + yy + 2 * s);
  if (t <= 0) return null;

  return { xx: (xx + s) / t, xy: xy / t, yy: (yy + s) / t };
}

/** Distance from the solid's centre to its furthest vertex. */
function boundingRadius(mesh: Mesh): number {
  let largest = 0;
  for (const vertex of mesh.vertices) {
    const distance = Math.hypot(vertex.x, vertex.y, vertex.z);
    if (distance > largest) largest = distance;
  }
  return largest;
}
